/**
 * Fixing a delivery that was keyed in wrong.
 *
 * These get typed at the back door with a box in one hand, so a 10 that should
 * have been 100, or a price read off the wrong invoice line, is ordinary. Up
 * to now there was no way to correct one, and a wrong delivery is not
 * cosmetic: it moves the stock figure, the ingredient's average cost, every
 * recipe costed from it and every margin on the dashboard.
 *
 * Opened from the line itself on "What came in", because that is the page
 * where somebody notices the number is wrong.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { formatMoney, type StockHistoryLine } from '@bakery/shared';

import { api } from '../lib/api.js';
import { Button, Field, Modal, MoneyInput, NumberInput, useToast } from '../components/ui.js';

export function EditDeliveryModal({ line, onClose }: { line: StockHistoryLine; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [qty, setQty] = useState(line.qty);
  /*
   * Priced per unit, which is how a delivery is nearly always read off an
   * invoice — and what makes a quantity fix behave: correct 100 kg to 10 and
   * the lot total follows, rather than restating 10 kg as having cost what
   * 100 did.
   */
  const [unitPrice, setUnitPrice] = useState(line.unitPrice ?? 0);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['stock-history'] });
    queryClient.invalidateQueries({ queryKey: ['ingredients'] });
    queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
  };

  const save = useMutation({
    mutationFn: () => api.inventory.editDelivery(line.lineId!, { qty, unitPrice }),
    onSuccess: () => {
      refresh();
      toast.push({
        tone: 'ok',
        title: `${line.ingredientName} corrected`,
        body: `Now ${qty} ${line.unit} at ${formatMoney(unitPrice)}/${line.unit}. Stock and cost have moved with it.`,
      });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not correct it', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: () => api.inventory.removeDelivery(line.lineId!),
    onSuccess: (result) => {
      refresh();
      toast.push({
        tone: 'ok',
        title: `${line.ingredientName} delivery removed`,
        body: result.purchaseDeleted
          ? 'It was the only line, so the whole delivery went with it. The stock it added has gone back out.'
          : 'The stock it added has gone back out.',
      });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove it', body: error.message }),
  });

  const busy = save.isPending || remove.isPending;
  const total = Math.round(qty * unitPrice);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Correct ${line.ingredientName}`}
      description="What was actually received, and what it actually cost. Stock and the ingredient's average cost move with it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={busy || qty <= 0} onClick={() => save.mutate()}>
            Save correction
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`Quantity received (${line.unit})`}>
            <NumberInput value={qty} onChange={setQty} className="tabular text-right" />
          </Field>
          <Field label={`Price per ${line.unit}`}>
            <MoneyInput value={unitPrice} onChange={setUnitPrice} />
          </Field>
        </div>

        <p className="text-sm text-[var(--color-ink-soft)]">
          That makes this delivery <strong className="tabular">{formatMoney(total)}</strong>
          {line.lineTotal != null && total !== line.lineTotal && (
            <> — it is recorded as {formatMoney(line.lineTotal)} now.</>
          )}
        </p>

        {/*
          * Removing is for the delivery keyed in twice or logged against the
          * wrong day. It takes the stock back out, so it asks first.
          */}
        <div className="border-t border-[var(--color-line)] pt-4">
          {confirmingRemoval ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[var(--color-ink-soft)]">
                Remove this delivery and take the {line.qty} {line.unit} back out of stock?
              </span>
              <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
                Remove
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmingRemoval(false)}>
                Keep it
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 className="size-4" />}
              disabled={busy}
              onClick={() => setConfirmingRemoval(true)}
            >
              This delivery never happened
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
